/**
 * The document's colour scheme for every mount (kernel apps, WS-14 section 3.6; layered shell
 * surfaces, WS-8 section 3.3, R-22), moved from the shell's `services/polarity-service.ts`.
 *
 * One controller per document owns the `dark` class on `<html>`; each mount reads it through a
 * view, and only the routed root may change it. The controller is itself a
 * `SurfacePolarityService`, so a host hands it to the chrome's `ChromeServices.polarity` as it is
 * (WS-15 R-1 (3)). The resolution rule is `resolvePolarity` of `@zion/shell-chrome/model`, so the
 * chrome and the host resolve one preference alike.
 */
import type { ResolvedPolarity, ShellPolarity } from '@zion/sdk-contracts';
import type { SurfacePolarityService, SurfacePolarityState } from '@zion/sdk-surfaces/v1';
import { resolvePolarity } from '@zion/shell-chrome/model';
import { SurfaceServiceRefusedError, createListenerSet, type MountScope } from './kit.js';
import { LEGACY_STORAGE_KEYS, STORAGE_KEYS, readMigrated } from './storage-keys.js';

/** The media query the `system` preference follows. */
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/** The three preferences a caller may store. */
const POLARITIES: readonly ShellPolarity[] = ['system', 'light', 'dark'];

/**
 * The document-wide polarity owner.
 *
 * - `current()` returns `{ preference, resolved }`; the object keeps its identity until either
 *   field changes.
 * - `set(next)` stores `next` under `STORAGE_KEYS.polarity`, swallowing a storage refusal,
 *   reapplies the class and notifies once when the state changed. A value outside `system`,
 *   `light` and `dark` throws `value-invalid` and changes nothing.
 * - `subscribe` hears every state change, from `set` or from the media query under `system`.
 * - `view(scope)` is the mount's {@link SurfacePolarityService}: its `set` throws `root-only`
 *   unless the mount is the routed root, checked before the value. Its subscriptions end with the
 *   mount.
 * - `dispose()` removes the media listener and every subscriber, and leaves the class as it is.
 */
export interface PolarityController extends SurfacePolarityService {
  current(): SurfacePolarityState;
  set(next: ShellPolarity): void;
  subscribe(listener: () => void): () => void;
  view(scope: MountScope): SurfacePolarityService;
  dispose(): void;
}

function readStoredPreference(): ShellPolarity {
  const stored = readMigrated(localStorage, STORAGE_KEYS.polarity, LEGACY_STORAGE_KEYS.polarity);
  return POLARITIES.find((p) => p === stored) ?? 'system';
}

function applyClass(resolved: ResolvedPolarity): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

/** True for a mount whose placement is routed with `root: true`. */
export function isRoutedRoot(scope: MountScope): boolean {
  const placement = scope.request.placement;
  return placement.kind === 'routed' && placement.root;
}

/**
 * Build the controller. It reads the stored preference (migrating the legacy key forward; a
 * garbage value or a refusing `localStorage` reads `system`), subscribes to
 * {@link DARK_SCHEME_QUERY} and applies the `dark` class before it returns, so the class is right
 * before any mount.
 *
 * @param matchMedia The window's `matchMedia`, or a fake with the same contract.
 */
export function createPolarityController(matchMedia: (query: string) => MediaQueryList): PolarityController {
  const media = matchMedia(DARK_SCHEME_QUERY);
  const listeners = createListenerSet();
  let preference = readStoredPreference();
  let state: SurfacePolarityState = Object.freeze({ preference, resolved: resolvePolarity(preference, media.matches) });
  applyClass(state.resolved);

  const recompute = (): void => {
    const resolved = resolvePolarity(preference, media.matches);
    if (state.preference === preference && state.resolved === resolved) return;
    state = Object.freeze({ preference, resolved });
    applyClass(resolved);
    listeners.notify();
  };
  const onMediaChange = (): void => recompute();
  media.addEventListener('change', onMediaChange);

  const set = (next: ShellPolarity): void => {
    if (!POLARITIES.includes(next)) {
      throw new SurfaceServiceRefusedError('value-invalid', 'polarity.set takes system, light or dark');
    }
    preference = next;
    try {
      localStorage.setItem(STORAGE_KEYS.polarity, next);
    } catch {
      // Storage refuses in private mode and at quota; the preference still holds for this page.
    }
    recompute();
  };

  return {
    current: () => state,
    set,
    subscribe: (listener) => listeners.add(listener),
    view(scope) {
      return scope.guard({
        current: () => state,
        set: (next: ShellPolarity) => {
          if (!isRoutedRoot(scope)) {
            throw new SurfaceServiceRefusedError('root-only', 'polarity.set is the routed root’s');
          }
          set(next);
        },
        subscribe: (listener: () => void) => {
          const remove = listeners.add(listener);
          scope.own(remove);
          return remove;
        },
      });
    },
    dispose() {
      media.removeEventListener('change', onMediaChange);
      listeners.clear();
    },
  };
}
